// eslint-disable-next-line no-unused-vars
import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import { requestStarted, requestFinished } from '~/stores/redux/common/loadingSlice';
import currencyOfferApi from './currencyOffer.api';
import { alertSwal } from '~/utils/helpers';
import { handleThunkException } from '~/services/thunk.service';

export const fetchCurrencyOfferApi = createAsyncThunk(
    'shopSaga/currencyOffer/fetchCurrencyOfferApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const data = await currencyOfferApi.index();
            if (data) {
                dispatch(requestFinished());
                return data;
            }
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        }
    },
);

export const createCurrencyOfferApi = createAsyncThunk(
    'shopSaga/currencyOffer/createCurrencyOfferApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const { data } = await currencyOfferApi.create();

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const storeCurrencyOfferApi = createAsyncThunk(
    'shopSaga/currencyOffer/storeCurrencyOfferApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, values } = params;

        try {
            dispatch(requestStarted());
            const currencyOffer = await currencyOfferApi.store(values);
            alertSwal('success', 'CurrencyOffer created successfully!');

            return currencyOffer;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const showCurrencyOfferApi = createAsyncThunk(
    'shopSaga/currencyOffer/showCurrencyOfferApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { id, navigate } = params;

        try {
            dispatch(requestStarted());
            const data = await currencyOfferApi.show(id);

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const updateCurrencyOfferApi = createAsyncThunk(
    'shopSaga/currencyOffer/updateCurrencyOfferApi',
    async (params, { dispatch, getState, rejectWithValue }) => {
        const { navigate, id, values } = params;
        const state = getState();

        try {
            dispatch(requestStarted());
            await currencyOfferApi.update(id, values);
            alertSwal('success', 'CurrencyOffer updated successfully!');

            const { ranks } = state.shopSaga.currencyOfferMetaData;
            const updatedRank = ranks.find((item) => item.id === values.rankId);

            return { values, updatedRank };
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const deleteCurrencyOfferApi = createAsyncThunk('shopSaga/currencyOffer/deleteCurrencyOfferApi', async (params, { dispatch }) => {
    const { navigate, id } = params;

    try {
        await currencyOfferApi.delete(id);

        return id;
    } catch (error) {
        handleThunkException(error, dispatch, navigate);
    }
});

export const currencyOfferSlice = createSlice({
    name: 'shopSaga/currencyOffer',
    initialState: { rows: [], model: {}, editedModel: {}, pictureAttributeNames: [] }, //model is initialValues that are used for formik.
    reducers: {
        clearEditedCurrencyOffer(state) {
            state.editedModel = {};
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCurrencyOfferApi.fulfilled, (state, { payload }) => {
                return {
                    ...state,
                    rows: payload,
                };
            })
            .addCase(fetchCurrencyOfferApi.rejected, (state, { payload = [] }) => {
                state.rows = payload;
            });

        builder
            .addCase(createCurrencyOfferApi.fulfilled, (state, { payload }) => {
                state.model = payload.model;
                state.pictureAttributeNames = payload.pictureAttributeNames;
            })
            .addCase(createCurrencyOfferApi.rejected, () => {
                //
            });

        builder
            .addCase(storeCurrencyOfferApi.fulfilled, (state) => {
                return {
                    ...state,
                    rows: [], //clear all rows so that api fetchAll will be called again in index page.
                };
            })
            .addCase(storeCurrencyOfferApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(showCurrencyOfferApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload;
            })
            .addCase(showCurrencyOfferApi.rejected, () => {
                //
            });

        builder
            .addCase(updateCurrencyOfferApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload; //this is to make Update component re-render so that the checkDuplicateName() will have the latest `initialName`.

                if (state.rows.length === 0) return;

                const updatedRow = state.rows.find((row) => row.id === payload.values.id);
                updatedRow.name = payload.values.name;
                updatedRow.statPoint = payload.values.statPoint;
                updatedRow.rank = payload.updatedRank.name;
            })
            .addCase(updateCurrencyOfferApi.rejected, () => {
                //
            });

        builder.addCase(deleteCurrencyOfferApi.fulfilled, (state, { payload }) => {
            return {
                ...state,
                rows: state.rows.filter(({ id }) => id !== payload),
            };
        });
    },
});

export const { clearEditedCurrencyOffer } = currencyOfferSlice.actions;
